import {
  CalculateResponse,
  RefundPaymentMethod,
  ReturnMethod
} from '@itsrever/returns-api-types/'
import { useTranslate } from '@/hooks'
import { Tooltip } from '@heroui/react'
import { InfoCircleIcon } from '@/ui/icons/info-circle'

interface ReturnDetailsProps {
  summary: CalculateResponse
}

export const ReturnDetails = ({ summary }: ReturnDetailsProps) => {
  const { translate } = useTranslate()

  const returnMethod =
    summary?.signed_summary?.return_summary?.return?.return_method

  const returnLocationsUrl =
    summary?.signed_summary?.return_summary?.return?.locations_url

  const returnAddress = summary?.signed_summary?.return_summary?.pickup_address

  const refundMethod =
    summary?.signed_summary?.return_summary?.refund?.refund_method

  const totalRefundAmount =
    summary?.signed_summary?.return_summary?.total_refund_amount ?? 0

  const isKeepYourItem =
    summary?.signed_summary?.return_summary?.keep_your_item ===
    'WITHOUT_VALIDATION'

  return (
    <div className="space-y-3">
      <h2
        className="text-base font-medium"
        dangerouslySetInnerHTML={{
          __html: translate('confirmation_page_return_details_title')
        }}
      />
      <div className="divide-y divide-solid divide-neutral-200 rounded-md border border-solid border-neutral-200">
        {returnMethod !== undefined && (
          <>
            <div className="flex items-start gap-3 px-4 py-4 md:items-center">
              <div className="flex flex-col gap-y-1 md:flex-row md:items-center md:gap-x-4">
                <span
                  className="min-w-28 whitespace-normal text-sm font-normal text-neutral-500"
                  dangerouslySetInnerHTML={{
                    __html: translate('confirmation_page_return_method')
                  }}
                />
                {isKeepYourItem ? (
                  <div className="flex items-center gap-2">
                    <span
                      className="flex-1 whitespace-nowrap text-sm font-medium"
                      dangerouslySetInnerHTML={{
                        __html: translate('kyi_title')
                      }}
                    />
                    <Tooltip content={translate('kyi_summary_description')}>
                      <button type="button">
                        <InfoCircleIcon className="size-4" />
                      </button>
                    </Tooltip>
                  </div>
                ) : (
                  <span
                    className="flex-1 whitespace-nowrap text-sm font-medium"
                    dangerouslySetInnerHTML={{
                      __html: translate(
                        `return_card_method${returnMethod}_title`
                      )
                    }}
                  />
                )}
              </div>
              {(returnMethod === ReturnMethod.ReturnMethod_DROP_OFF_POINT ||
                returnMethod === ReturnMethod.ReturnMethod_IN_STORE) &&
                returnLocationsUrl && (
                  <a
                    href={returnLocationsUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="ml-auto text-right text-xs font-normal text-neutral-500 underline underline-offset-2"
                    dangerouslySetInnerHTML={{
                      __html: translate('confirmation_page_locations_link')
                    }}
                  />
                )}
            </div>
            {returnMethod !== ReturnMethod.ReturnMethod_DROP_OFF_POINT &&
              returnMethod !== ReturnMethod.ReturnMethod_IN_STORE &&
              returnMethod !== ReturnMethod.ReturnMethod_NO_RETURN_METHOD && (
                <div className="flex flex-col gap-y-1 px-4 py-3 md:flex-row md:items-center md:gap-x-4">
                  <span
                    className="min-w-28 shrink-0 text-sm font-normal text-neutral-500"
                    dangerouslySetInnerHTML={{
                      __html: translate(
                        'confirmation_page_return_address_title'
                      )
                    }}
                  />
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium">
                      {returnAddress?.address_line_1}, {returnAddress?.postcode}{' '}
                      {returnAddress?.city}, {returnAddress?.country}
                    </span>
                  </div>
                </div>
              )}
          </>
        )}
        <div className="flex flex-col gap-y-1 px-4 py-4 md:flex-row md:items-center md:gap-x-4">
          <span
            className="min-w-28 shrink-0 text-sm font-normal text-neutral-500"
            dangerouslySetInnerHTML={{
              __html: translate('confirmation_page_refund_method')
            }}
          />
          {totalRefundAmount > 0 ? (
            <span
              className="flex-1 text-sm font-medium"
              dangerouslySetInnerHTML={{
                __html: translate(`confirmation_page_method${refundMethod}`)
              }}
            />
          ) : (
            <span
              className="flex-1 text-sm font-medium"
              dangerouslySetInnerHTML={{
                __html: translate(
                  `confirmation_page_method${RefundPaymentMethod.RefundPaymentMethod_NO_PAYMENT_METHOD}`
                )
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}
